import _ from 'underscore';
import {
  RECOMMENDATION_CATEGORIES_IDS,
  RECOMMENDATION_TYPES,
  normalize,
  formatCoupon,
} from '@bingads-webui-campaign/recommendation-core';
import {
  fetchCoupons,
} from '@bingads-webui-campaign/overview-cards-common';
import {
  getCcJsData,
  fetchRecommendations,
  clearRecommendationsCache,
  fetchTips,
  fetchMCCOverviewRecommendations,
} from '@bingads-webui-campaign/fluent-page-data';
import { tipsConfig, tipNames } from '@bingads-webui-campaign/overview-recommendations-config';

import {
  FAC_FEATUREID_TIPID_PRIORITY_MAPPING,
  FAC_TIPID_FEATUREID_MAPPING,
  logApi,
  numberOfTipsAndRecommendations,
  numberOfAccountRecommendations,
  numberOfCustomerRecommendations,
  numberOfCampaignRecommendations,
  numberOfTipsShown,
} from './const';

// @ts-ignore
const i18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/dashboard-cards');

export function logAdInsightOppTracking({
  oppt,
  isClick,
  position,
  instrumentationLegacy,
  instrumentationConstants,
  campaignId,
  guid,
  isMCC,
}) {
  instrumentationLegacy.logMessage([instrumentationConstants.LOGCATEGORY.TRACE], instrumentationConstants.LOGLEVEL.INFO, isMCC ? 'MCCOverviewOpportunityTile' : 'OverviewOpportunityTile', {
    type: oppt.opportunityType || oppt.type,
    id: 0,
    campaignId,
    isClick,
    guid,
    position,
    typeId: oppt.opportunityTypeId,
  });
}


export function logSlideDisplay({
  instrumentationLegacy,
  instrumentationConstants,
  reason,
  activeItems,
  currentSlide,
  campaignId,
  guid,
  isMCC,
}) {
  if (activeItems.length !== 0 && activeItems[currentSlide].type && activeItems[currentSlide].type.indexOf('Opportunity') !== -1) {
    const opportunityType = activeItems[currentSlide].opportunityType ? activeItems[currentSlide].opportunityType : activeItems[currentSlide].type;

    instrumentationLegacy.logMessage(
      [instrumentationConstants.LOGCATEGORY.TRACE],
      instrumentationConstants.LOGLEVEL.INFO,
      logApi,
      `Opportunity displayed. Opportunity Id: ${opportunityType} . Reason: ${reason}.`
    );
    logAdInsightOppTracking({
      oppt: activeItems[currentSlide],
      isClick: false,
      position: currentSlide + 1,
      instrumentationLegacy,
      instrumentationConstants,
      campaignId,
      guid,
      isMCC,
    });
  } else {
    const tipId = activeItems.length === 0 ? 0 : activeItems[currentSlide].tipId;

    instrumentationLegacy.logMessage(
      [instrumentationConstants.LOGCATEGORY.TRACE],
      instrumentationConstants.LOGLEVEL.INFO,
      logApi,
      `Tip displayed. Tip Id: ${tipId} . Reason: ${reason}.`
    );
  }
}


function willDisplayImportRelatedtips({
  tips,
}: {
  tips: {
    isImportRelatedTip: boolean,
  }[],
}) {
  const importRelatedTips = _.filter(tips, tip => tip.isImportRelatedTip);

  return importRelatedTips.length !== 0;
}


function getRecommendationCounts({
  tips,
  recommendations,
}) {
  const tipsCount = _.size(tips);

  const recommendationsCount = _.size(recommendations);
  let recommendationsToShow = numberOfAccountRecommendations;

  // if we do not have enough tips, show recommendations instead.
  if (tipsCount < numberOfTipsShown) {
    recommendationsToShow += numberOfTipsShown - tipsCount;
  }

  return { recommendationsCount, recommendationsToShow };
}

function getCarouselActiveItems({
  navigationLevel,
  tips,
  recommendations,
  permissions,
  instrumentationLegacy,
  instrumentationConstants,
  campaignId,
  guid,
  isMCC,
  overallOptimizationScoreBar,
}: {
  navigationLevel: string,
  tips: {
    tipId: string,
    type: string,
    tipName: string,
    helpType: string,
    isImportRelatedTip: boolean,
  }[],
  recommendations: any,
  permissions: any,
  instrumentationLegacy: any,
  instrumentationConstants: any,
  campaignId: number,
  guid: string,
  isMCC: boolean,
  overallOptimizationScoreBar?: object,
}) {
  let allItems;
  if (navigationLevel === 'account') {
    const { recommendationsCount, recommendationsToShow } = getRecommendationCounts({
      tips,
      recommendations,
    });

    if (recommendationsToShow < recommendationsCount) {
      allItems = [
        ..._.first(recommendations, recommendationsToShow),
        ...tips,
        ..._.last(recommendations, recommendationsCount - recommendationsToShow),
      ];
    } else {
      allItems = [..._.first(recommendations, recommendationsToShow), ...tips];
    }

    if (permissions.IsImportTipsPriorityEnabled) {
      const otherThanImportTips = _.filter(allItems, tip => !tip.isImportRelatedTip);

      const importTips = _.filter(allItems, tip => tip.isImportRelatedTip);

      allItems = [
        ...importTips,
        ...otherThanImportTips,
      ];
    }
  } else {
    allItems = recommendations;
  }
  const numberOfRecommendations = navigationLevel === 'customer' ? numberOfCustomerRecommendations : numberOfCampaignRecommendations;

  const activeItems = _.first(allItems, navigationLevel === 'account' ? numberOfTipsAndRecommendations : numberOfRecommendations);

  const loaded = true;
  const currentSlide = 0;

  logSlideDisplay({
    reason: 'initial load',
    instrumentationLegacy,
    instrumentationConstants,
    activeItems,
    currentSlide,
    campaignId,
    guid,
    isMCC,
  });

  return {
    allItems,
    activeItems,
    loaded,
    currentSlide,
    overallOptimizationScoreBar,
  };
}

function getCarouselActiveItemsWithCouponDataInternal({
  p$Coupons,
  serverContext,
  tips,
  navigationLevel,
  recommendations,
  permissions,
  instrumentationLegacy,
  instrumentationConstants,
  campaignId,
  guid,
  isMCC,
  overallOptimizationScoreBar,
}) {
  return p$Coupons.then((coupons) => {
    let newTips = tips;
    if (coupons.length) {
      const rawCoupon = coupons[0];
      const coupon = formatCoupon({
        i18n,
        coupon: rawCoupon,
        currency: serverContext.CurrentAccount.CurrencyCode,
      });
      const couponFeatureId = rawCoupon.FeatureId.toString();
      const tipPriorities = FAC_FEATUREID_TIPID_PRIORITY_MAPPING[couponFeatureId];

      // check if a coupon is valid for tips
      const couponEligibleTip = _.chain(tips)
        .filter(tip => _.contains(tipPriorities, tip.tipId))
        .sortBy(tip => _.indexOf(tipPriorities, tip.tipId))
        .first()
        .value();

      if (couponEligibleTip) {
        const tipWithCoupon = {
          ...couponEligibleTip,
          coupon,
        };
        // move tip with coupon to front of tips
        newTips = [tipWithCoupon, ..._.reject(tips, tip => tip === couponEligibleTip)];
      }
    }

    return getCarouselActiveItems({
      tips: newTips,
      navigationLevel,
      recommendations,
      permissions,
      instrumentationLegacy,
      instrumentationConstants,
      campaignId,
      guid,
      isMCC,
      overallOptimizationScoreBar,
    });
  });
}

// previous handleCouponsThenSetUpCarousel function
function getCarouselActiveItemsWithCouponData({
  isMCC,
  serverContext,
  tips,
  navigationLevel,
  recommendations,
  permissions,
  instrumentationLegacy,
  instrumentationConstants,
  campaignId,
  guid,
  overallOptimizationScoreBar,
}: {
  isMCC: boolean,
  serverContext: any,
  tips: {
    tipId: string,
    type: string,
    tipName: string,
    helpType: string,
    isImportRelatedTip: boolean,
    coupon?: any,
  }[],
  navigationLevel: string,
  recommendations: any,
  permissions: any,
  instrumentationLegacy: any,
  instrumentationConstants: any,
  campaignId: number,
  guid: string,
  overallOptimizationScoreBar?: object,
}) {
  const ccJsData = getCcJsData({ serverContext });
  if (!isMCC && _.any(tips, tip => _.has(FAC_TIPID_FEATUREID_MAPPING, tip.tipId))) {
    const p$Coupons = fetchCoupons({
      ccJsData,
    });

    return getCarouselActiveItemsWithCouponDataInternal({
      p$Coupons,
      serverContext,
      tips,
      navigationLevel,
      recommendations,
      permissions,
      instrumentationLegacy,
      instrumentationConstants,
      campaignId,
      guid,
      isMCC,
      overallOptimizationScoreBar,
    });
  }

  return getCarouselActiveItems({
    navigationLevel,
    tips,
    recommendations,
    permissions,
    instrumentationLegacy,
    instrumentationConstants,
    campaignId,
    guid,
    isMCC,
    overallOptimizationScoreBar,
  });
}

// previous loadOpportunitiesData function
function getRecommendationsFromOpportunitiesData({
  opportunitiesData,
}) {
  // TODO: [yaoyao] do we need this in new overview page?
  // dataFetched = true;
  const categories = _.invert(RECOMMENDATION_CATEGORIES_IDS);

  let recommendations = _.map(normalize(opportunitiesData) as {
    category: string,
    type: string,
  }[], oppt => ({
    ...oppt,
    category: categories[oppt.category],
  }));

  recommendations = _.compact(recommendations);
  const accountRecommendationIdsToShow = _.chain(recommendations)
    .first(numberOfAccountRecommendations)
    // eslint-disable-next-line no-confusing-arrow
    .map(recommendation => recommendation.type)
    .value();
  const overallOptimizationScoreBar = opportunitiesData.OverallOptimizationScoreBar;

  return {
    recommendations,
    accountRecommendationIdsToShow,
    overallOptimizationScoreBar,
  };
}


function willDisplayAccountRecommendation({
  type,
  accountRecommendationIdsToShow,
}) {
  if (_.isArray(type)) {
    return !_.chain(accountRecommendationIdsToShow).intersection(type).isEmpty().value();
  }

  return _.contains(accountRecommendationIdsToShow, type);
}

// Filter tips based on language and market and recommendations

function shouldShowTip({
  tipName,
  helpType,
  permissions,
  serverContext,
  accountRecommendationIdsToShow,
}) {
  const market = serverContext.CurrentUser.Market;
  const language = serverContext.CurrentUser.Language;

  if ((tipName === tipNames.calloutsMktg || tipName === tipNames.calloutsHelp) && market === 'CN') {
    return false;
  }

  if (tipName === tipNames.dsa && (market !== 'US' && market !== 'GB')) {
    return false;
  }

  const autoBidMarket = ['AU', 'CA', 'CH', 'DE', 'ES', 'FR', 'GB', 'IN', 'IT', 'NL', 'SE', 'US'];

  if ((tipName === tipNames.autoBidHelp || tipName === tipNames.autoBidMktg) && _.indexOf(autoBidMarket, market) === -1) {
    return false;
  }

  if ((tipName === tipNames.autoBidMktg || helpType === 'video') && language !== 'EN') {
    return false;
  }

  if (helpType === 'tour') {
    return false;
  }

  if ((tipName === tipNames.importHelp || tipName === tipNames.importVideo) &&
    willDisplayAccountRecommendation({ type: RECOMMENDATION_TYPES.GOOGLE_IMPORT, accountRecommendationIdsToShow }) &&
    !permissions.IsImportTipsPriorityEnabled) {
    return false;
  }

  if (tipName === tipNames.broadMatchVideo &&
    willDisplayAccountRecommendation({ type: RECOMMENDATION_TYPES.BROAD_MATCH_MODIFIER_KEYWORD, accountRecommendationIdsToShow })) {
    return false;
  }

  if ((tipName === tipNames.calloutsMktg || tipName === tipNames.calloutsHelp) &&
    willDisplayAccountRecommendation({ type: RECOMMENDATION_TYPES.CALLOUT, accountRecommendationIdsToShow })) {
    return false;
  }

  if (tipName === tipNames.dsa &&
    willDisplayAccountRecommendation({ type: RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS, accountRecommendationIdsToShow })) {
    return false;
  }

  if ((tipName === tipNames.autoBidHelp || tipName === tipNames.autoBidMktg) &&
    willDisplayAccountRecommendation({
      type: [
        RECOMMENDATION_TYPES.AUTOBIDDING_ENHANCEDCPC,
        RECOMMENDATION_TYPES.AUTOBIDDING_MAXCLICKS,
        RECOMMENDATION_TYPES.AUTOBIDDING_MAXCONVERSIONS,
        RECOMMENDATION_TYPES.AUTOBIDDING_TARGETCPA,
      ],
      accountRecommendationIdsToShow,
    })) {
    return false;
  }

  if (tipName === tipNames.broadMatchHelp &&
    willDisplayAccountRecommendation({ type: RECOMMENDATION_TYPES.BROAD_MATCH_KEYWORD, accountRecommendationIdsToShow })) {
    return false;
  }

  if ((tipName === tipNames.sitelinksMktg || tipName === tipNames.sitelinksHelp) &&
    willDisplayAccountRecommendation({ type: RECOMMENDATION_TYPES.SITE_LINK, accountRecommendationIdsToShow })) {
    return false;
  }

  if (tipName === tipNames.ScheduledImportHelp &&
    willDisplayAccountRecommendation({ type: RECOMMENDATION_TYPES.GOOGLE_IMPORT_SCHEDULED, accountRecommendationIdsToShow })) {
    return false;
  }

  return true;
}


function loadTipsData({
  tipList,
  recommendations,
  permissions,
  serverContext,
  accountRecommendationIdsToShow,
}): {
  tips: {
    tipId: string,
    type: string,
    tipName: string,
    helpType: string,
    isImportRelatedTip: boolean,
  }[],
  recommendations: any
} {
  const tips = _.chain(tipList)
    .map((tip) => {
      if (tip == null) {
        return null;
      }
      const { TipId } = tip;
      const tipConfig = tipsConfig[TipId];

      if (tipConfig) {
        return _.extend(
          tipConfig,
          {
            tipId: TipId,
            type: 'Tip',
          }
        );
      }

      return null;
    })
    .compact()
    .filter(({ tipName, helpType }) => shouldShowTip({
      tipName,
      helpType,
      permissions,
      serverContext,
      accountRecommendationIdsToShow,
    }))
    .shuffle()
    .value();

  let newRecommendations = recommendations;
  if (permissions.IsImportTipsPriorityEnabled && willDisplayImportRelatedtips({ tips })) {
    // filter out import related recommendations
    // if we are showing import related tips.
    newRecommendations = _.filter(recommendations, recommendation => recommendation !== RECOMMENDATION_TYPES.GOOGLE_IMPORT);
  }

  return {
    tips,
    recommendations: newRecommendations,
  };
}


// load tips data if we have more tips else load opportunities.
function loadTipAndOpportunities({
  p$Tips,
  opportunitiesData,
  permissions,
  serverContext,
  isMCC,
  navigationLevel,
  instrumentationLegacy,
  instrumentationConstants,
  campaignId,
  guid,
}) {
  if (p$Tips) {
    return p$Tips.then((tipList) => {
      // need to load recommendations first to filter tips based on available opportunities
      const {
        recommendations: recommendationsOriginal,
        accountRecommendationIdsToShow,
        overallOptimizationScoreBar,
      } = getRecommendationsFromOpportunitiesData({
        opportunitiesData,
      });
      const {
        tips,
        recommendations,
      } = loadTipsData({
        tipList,
        recommendations: recommendationsOriginal,
        permissions,
        serverContext,
        accountRecommendationIdsToShow,
      });
      return getCarouselActiveItemsWithCouponData({
        isMCC,
        serverContext,
        tips,
        navigationLevel,
        recommendations,
        permissions,
        instrumentationLegacy,
        instrumentationConstants,
        campaignId,
        guid,
        overallOptimizationScoreBar,
      });
    });
  }
  const {
    recommendations,
    overallOptimizationScoreBar,
  } = getRecommendationsFromOpportunitiesData({
    opportunitiesData,
  });
  return getCarouselActiveItemsWithCouponData({
    isMCC,
    serverContext,
    tips: [],
    navigationLevel,
    recommendations,
    permissions,
    instrumentationLegacy,
    instrumentationConstants,
    campaignId,
    guid,
    overallOptimizationScoreBar,
  });
}


function getRecommendationsData({
  entitySet,
  navigationLevel,
  featureInstrumentation,
  legacyConfig,
  campaignType,
  serverContext,
  jsdata,
  permissions,
  cid,
  aid,
  campaignId,
}) {
  if (navigationLevel === 'customer') {
    return fetchMCCOverviewRecommendations({
      entitySet,
      jsdata,
      permissions,
    });
  }
  const ccJsData = getCcJsData({ serverContext });

  return fetchRecommendations({
    navigationLevel,
    featureInstrumentation,
    legacyConfig,
    permissions,
    serverContext,
    campaignType,
    ccJsData,
    cid,
    aid,
    campaignId,
  });
}

// previous loadOpportunities function
// the return value is a promise from getCarouselActiveItems with a format:
// {
//   allItems,
//   activeItems,
//   loaded,
//   currentSlide,
// }
// plus a new key overallOptimizationScoreBar
export function getCardData({
  entitySet,
  navigationLevel,
  featureInstrumentation,
  legacyConfig,
  campaignType,
  serverContext,
  jsdata,
  permissions,
  cid,
  aid,
  campaignId,
  instrumentationLegacy,
  instrumentationConstants,
  isMCC,
  guid,
}) {
  let p$Tips;

  if (navigationLevel === 'account') {
    p$Tips = fetchTips({
      aid,
      cid,
    });
  }

  return getRecommendationsData({
    entitySet,
    navigationLevel,
    featureInstrumentation,
    legacyConfig,
    campaignType,
    serverContext,
    jsdata,
    permissions,
    cid,
    aid,
    campaignId,
  }).then((response) => {
    const opportunitiesData = navigationLevel === 'customer' ? { value: response.Recommendations?.value } : response.data;

    return loadTipAndOpportunities({
      p$Tips,
      opportunitiesData,
      permissions,
      serverContext,
      isMCC,
      navigationLevel,
      instrumentationLegacy,
      instrumentationConstants,
      campaignId,
      guid,
    });
  }).catch((response) => {
    // no need to log ajax error here.
    if (response.status === undefined) {
      instrumentationLegacy.logMessage([instrumentationConstants.LOGCATEGORY.ERROR], instrumentationConstants.LOGLEVEL.INFO, logApi, response.stack);
    }

    // If there are exceptions load tips
    return loadTipAndOpportunities({
      p$Tips,
      opportunitiesData: { values: [] },
      permissions,
      serverContext,
      isMCC,
      navigationLevel,
      instrumentationLegacy,
      instrumentationConstants,
      campaignId,
      guid,
    });
  })
    .finally(() => clearRecommendationsCache());
}
